import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { BellRinging, EnvelopeSimple, CircleNotch, PaperPlaneTilt, Warning, CheckCircle, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const PC = { gpt: "#10A37F", claude: "#D97757", gemini: "#8E24AA" };
const PL = { gpt: "GPT", claude: "Claude", gemini: "Gemini" };

function fmt(iso) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Settings() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [testing, setTesting] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data,
  });
  const { data: alerts } = useQuery({
    queryKey: ["alerts"], queryFn: async () => (await api.get("/alerts")).data,
  });

  useEffect(() => {
    if (settings && !form) setForm(settings);
  }, [settings, form, setForm]);

  const saveMut = useMutation({
    mutationFn: (body) => api.put("/settings", body),
    onSuccess: (r) => {
      qc.setQueryData(["settings"], r.data);
      setForm(r.data);
      toast.success("Settings saved");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/alerts/test");
      toast.success(`Test alert sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  if (!form) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-8 fade-in">
        <p className="overline text-action">Notifications</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter">Citation Alerts</h1>
        <p className="mt-1 text-sm text-muted-foreground">Get emailed when your citation rate drops sharply on any provider.</p>
      </div>

      {/* Email status banner */}
      {!settings?.email_configured && (
        <Card className="mb-6 flex items-center gap-3 border-yellow-600/40 bg-yellow-500/5 p-4" data-testid="email-not-configured">
          <Warning size={20} className="text-yellow-500" />
          <p className="text-sm text-yellow-200/90">
            Email sending isn't configured yet. Add a <b>RESEND_API_KEY</b> to the backend to enable delivery. Alerts will still be recorded below.
          </p>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border bg-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight">
                <BellRinging size={18} className="text-action" /> Alert rules
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">Checked automatically after every probe.</p>
            </div>
            <Switch
              checked={form.alerts_enabled}
              data-testid="alerts-enabled-switch"
              onCheckedChange={(v) => setForm({ ...form, alerts_enabled: v })}
            />
          </div>

          <Separator className="my-6 bg-border" />

          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><EnvelopeSimple size={15} /> Notification email</Label>
              <Input
                type="email"
                data-testid="notification-email-input"
                value={form.notification_email || ""}
                onChange={(e) => setForm({ ...form, notification_email: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Where alert emails are delivered.</p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><TrendDown size={15} /> Sharp-drop threshold</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min={1} max={100}
                    data-testid="drop-threshold-input"
                    value={form.drop_threshold}
                    onChange={(e) => setForm({ ...form, drop_threshold: Number(e.target.value) })}
                  />
                  <span className="text-sm text-muted-foreground">pts</span>
                </div>
                <p className="text-xs text-muted-foreground">Alert when a provider drops by this many points vs its prior period.</p>
              </div>
              <div className="space-y-2">
                <Label>Low-rate threshold</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min={1} max={100}
                    data-testid="low-threshold-input"
                    value={form.low_threshold}
                    onChange={(e) => setForm({ ...form, low_threshold: Number(e.target.value) })}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground">Alert when a provider's citation rate falls below this.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              className="bg-action text-white hover:bg-action/90"
              data-testid="save-settings-button"
              disabled={saveMut.isPending}
              onClick={() => saveMut.mutate({
                alerts_enabled: form.alerts_enabled,
                notification_email: form.notification_email,
                drop_threshold: form.drop_threshold,
                low_threshold: form.low_threshold,
              })}
            >
              {saveMut.isPending ? <CircleNotch size={18} className="animate-spin" /> : "Save changes"}
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-border"
              data-testid="test-alert-button"
              disabled={testing}
              onClick={sendTest}
            >
              {testing ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
              Send test alert
            </Button>
          </div>
        </Card>

        {/* Alert history */}
        <Card className="border-border bg-card p-6 lg:col-span-1">
          <p className="overline text-muted-foreground mb-4">Alert history</p>
          <div className="space-y-3" data-testid="alerts-history">
            {(alerts || []).map((a) => (
              <div key={a.id} className="border-b border-border/40 pb-3 last:border-0">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: PC[a.provider] }}>
                    <span className="h-2 w-2 rounded-full" style={{ background: PC[a.provider] }} /> {PL[a.provider]}
                  </span>
                  <Badge variant="outline" className={`text-[10px] border-0 ${a.status === "sent" ? "bg-success/15 text-success" : "bg-secondary text-muted-foreground"}`}>
                    {a.status}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{a.message}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground/70">{fmt(a.sent_at)}</p>
              </div>
            ))}
            {(!alerts || alerts.length === 0) && (
              <div className="flex flex-col items-center gap-2 py-8 text-center">
                <CheckCircle size={26} className="text-success" />
                <p className="text-sm text-muted-foreground">No alerts yet. All providers healthy.</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
